import type {
	DefaultError,
	QueryFunctionContext as TanstackQueryFunctionContext,
	QueryKey as TanstackQueryKey,
} from "@tanstack/query-core";
import { queryOptions as tanstackQueryOptions } from "@tanstack/react-query";

import type { UnionToTuple } from "@virtual-brain-lib/types";

import { QUERY_KEYS } from "./queryKeys";

type QueryKeys = typeof QUERY_KEYS;
type Key = keyof QueryKeys;

type QueryKeysParams<TKey extends Key, TValue = QueryKeys[TKey]> = TValue extends (
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	args: any,
) => TanstackQueryKey
	? Parameters<TValue>[0]
	: never;

type QueryOptions<
	TQueryFnData = unknown,
	TError = DefaultError,
	TData = TQueryFnData,
	TQueryKey extends TanstackQueryKey = TanstackQueryKey,
> = Parameters<typeof tanstackQueryOptions<TQueryFnData, TError, TData, TQueryKey>>[0];

type BaseQueryOptions<TQueryFnData, TError = DefaultError, TData = TQueryFnData> = Omit<
	QueryOptions<TQueryFnData, TError, TData>,
	"queryKey" | "queryFn"
>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type QueryKey<TKey extends Key> = QueryKeys[TKey] extends (...args: any) => TanstackQueryKey
	? UnionToTuple<ReturnType<QueryKeys[TKey]>[0]>
	: UnionToTuple<QueryKeys[TKey]>;

export type QueryFunctionContext<TKey extends Key> = TanstackQueryFunctionContext<QueryKey<TKey>>;

type Data<TQueryFnData, TOptions> = TOptions extends {
	select: (data: TQueryFnData) => infer TResult;
}
	? TResult
	: TQueryFnData;
/**
 * Use this function to create useQuery options
 *
 * You need to update {@link QUERY_KEYS} if you have new query
 *
 * @param queryKey
 * @param initialOptions
 */
export const queryOptions = <
	TQueryFnData,
	TKey extends Key,
	TError = DefaultError,
	TData = TQueryFnData,
>({
	queryKey: key,
	...initialOptions
}: Omit<QueryOptions<TQueryFnData, TError, TData, QueryKey<TKey>>, "queryKey"> & {
	queryKey: TKey;
}): QueryKeysParams<TKey> extends never
	? <TOptions extends BaseQueryOptions<TQueryFnData, TError, Data<TQueryFnData, TOptions>>>(
			options?: TOptions,
		) => ReturnType<
			typeof tanstackQueryOptions<TQueryFnData, TError, Data<TQueryFnData, TOptions>>
		>
	: <
			TOptions extends BaseQueryOptions<
				TQueryFnData,
				TError,
				Data<TQueryFnData, TOptions>
			> & {
				params: QueryKeysParams<TKey>;
			},
		>(
			options: TOptions,
		) => ReturnType<
			typeof tanstackQueryOptions<TQueryFnData, TError, Data<TQueryFnData, TOptions>>
		> => {
	const queryKey = QUERY_KEYS[key];

	// eslint-disable-next-line @typescript-eslint/ban-ts-comment
	// @ts-expect-error
	return (options) => {
		// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
		const { params, ...tanstackOptions } = options ?? {};

		// eslint-disable-next-line @typescript-eslint/no-unsafe-argument
		const finalQueryKey = typeof queryKey === "function" ? queryKey(params) : queryKey;

		// eslint-disable-next-line @typescript-eslint/no-unsafe-argument
		return tanstackQueryOptions({
			...initialOptions,
			...tanstackOptions,
			queryKey: finalQueryKey,
		});
	};
};
